import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
    LocaleProvider, mergeCatalogs, common, resolveLocale, readDeviceLocale, writeDeviceLocale,
    fetchMyUiLocale, saveMyUiLocale, type LocaleCode,
} from '@patienttrac/i18n';
import { supabase } from './supabase';
import { useAuth } from './auth';
import { orCatalog } from '../i18n/or';

// Estate locale contract (Foundation Increment 1) — same resolution as Forge and Hub:
//   pre-sign-in  → device memory (this domain) → browser language → en
//   post-sign-in → saas.my_ui_locale() (staff ui_locale → org default → en)
// Switching while signed in persists via saas.set_my_ui_locale().

const catalogs = mergeCatalogs(common, orCatalog);
const saas = () => supabase.schema('saas');

export function OrLocaleProvider({ children }: { children: ReactNode }) {
    const { session } = useAuth();
    const [locale, setLocaleState] = useState<LocaleCode>(() =>
        resolveLocale({ device: readDeviceLocale(), browser: typeof navigator !== 'undefined' ? navigator.language : null }));

    useEffect(() => {
        if (!session) return;
        let cancelled = false;
        (async () => {
            try {
                const staff = await fetchMyUiLocale(saas());
                if (cancelled) return;
                const next = resolveLocale({ staff, device: readDeviceLocale(), browser: navigator.language });
                setLocaleState(next); writeDeviceLocale(next);
            } catch (e) {
                console.error('[asc] my_ui_locale failed; keeping device/browser locale:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [session?.user.id]);

    const setLocale = useCallback((code: LocaleCode) => {
        setLocaleState(code); writeDeviceLocale(code);
        if (session) saveMyUiLocale(saas(), code).catch(e => console.error('[asc] set_my_ui_locale failed:', e));
    }, [session]);

    return (
        <LocaleProvider locale={locale} onLocaleChange={setLocale} catalogs={catalogs}>
            {children}
        </LocaleProvider>
    );
}
